// Copyright (c) 2018 Cigent Technology Inc. All Rights Reserved.

import { Component, OnInit, ViewChild } from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {CustomValidators} from '../../shared/validators/custom-validators';
import {UserService} from '../../../services/user-service';
import {PasswordRulesComponent} from '../../dialogs/password-rules/password-rules.component';
import {MatDialog} from '@angular/material/dialog';
import { BehaviorSubject, EMPTY } from 'rxjs';
import {ActivatedRoute} from '@angular/router';
import {NotificationService} from '../../../services/notification-service';
import {TranslateService} from '@ngx-translate/core';
import {D3eService} from '../../../services/d3e-service';
import { catchError, switchMap } from 'rxjs/operators';
import { ISoftwareVersion, ISubscription, ISubscriptionRequest, LicenseType } from '../../../types/subscription';
import {CreateUserStatus} from '../../../types/user';
import { UntilDestroy } from '@ngneat/until-destroy';
import { ConfigurationService } from '../../../services/configuration-service';
import { MatTooltip } from '@angular/material/tooltip';

enum ComponentState {
  wait,
  form,
  verify,
  download,
}

@UntilDestroy({ checkProperties: true })
@Component({
    selector: 'app-plus-trial',
    templateUrl: './plus-trial.component.html',
    styleUrls: ['./plus-trial.component.scss'],
    standalone: false
})
export class PlusTrialComponent implements OnInit {
  @ViewChild('tooltipNew') tooltipNew: MatTooltip;
  @ViewChild('tooltipConfirm') tooltipConfirm: MatTooltip;
  style = {
    'width': '565px',
    'margin': '80px auto 25px auto'
  }
  formGroup: UntypedFormGroup;
  codeFormGroup: UntypedFormGroup;
  readonly ComponentState = ComponentState;
  state: BehaviorSubject<ComponentState> = new BehaviorSubject<ComponentState>(ComponentState.wait);
  subscription: ISubscription;
  softwareVersion: ISoftwareVersion;
  partnerId = 0;
  // uniqueEmailValidator;
  capsOnNew: boolean = false;
  capsOnConfirm: boolean = false;
  passwordInputIsFocused: boolean;
  passwordConfirmInputIsFocused: boolean;

  private readonly PLAN_ID = "mspmanager_plan_business";

  constructor(private readonly formBuilder: UntypedFormBuilder,
              private readonly userService: UserService,
              private readonly notification: NotificationService,
              private readonly d3eService: D3eService,
              private readonly route: ActivatedRoute,
              private readonly translate: TranslateService,
              private readonly dialog: MatDialog,
              private readonly configService: ConfigurationService) {
    this.partnerId = configService.partnerId;
    // this.uniqueEmailValidator = new UniqueEmailValidator(userService, null);
    this.formGroup = this.formBuilder.group({
      firstName: ['', [Validators.required, Validators.maxLength(255)]],
      lastName: ['', [Validators.required, Validators.maxLength(255)]],
      email: ['', {
        validators: [
          Validators.required,
          Validators.pattern(CustomValidators.emailPattern),
          CustomValidators.maxByteLength(255)]
    }],
      company: ['', [Validators.required, Validators.maxLength(255)]],
      password: ['', [
        Validators.required,
        CustomValidators.has3Categories2(),
        CustomValidators.hasNoPipeSymbol(),
        CustomValidators.minByteLength(10),
        CustomValidators.maxByteLength(50),

      ]],
      confirmPassword: ['', Validators.required],
    }, {
      validator: [
        CustomValidators.passwordMatchValidator('password', 'confirmPassword'),
      ]
    });

    this.codeFormGroup = this.formBuilder.group({
      code: ['', [Validators.required, Validators.minLength(6)]]
    });

  }

  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      if (params.verify) {
        const email = decodeURIComponent(params.verify);
        this.formGroup.controls.email.setValue(email);
        this.state.next(ComponentState.verify);
      } else {
        this.state.next(ComponentState.form);
      }
    });
  }

  get firstName() {
    return this.formGroup.controls.firstName;
  }

  get lastName() {
    return this.formGroup.controls.lastName;
  }

  get email() {
    return this.formGroup.controls.email;
  }

  get company() {
    return this.formGroup.controls.company;
  }

  get password() {
    return this.formGroup.controls.password;
  }

  get confirmPassword() {
    return this.formGroup.controls.confirmPassword;
  }

  onSubmit() {
    this.state.next(ComponentState.wait);
    const request = {
      email: this.formGroup.value.email.trim(),
      password: this.formGroup.value.password,
      firstName: this.formGroup.value.firstName.trim(),
      lastName: this.formGroup.value.lastName.trim(),
      company: this.formGroup.value.company.trim(),
      zip: '',
      country: '',
      partnerId: this.partnerId
    };
    this.userService.createUser(request).pipe(
        catchError(() => {
          this.notification.showError(this.translate.instant('ERROR_OCCURRED_UNKNOWN'));
          this.state.next(ComponentState.form);
          return EMPTY;
        })
    ).subscribe(result => {
      if (result.status == CreateUserStatus.SUCCESS) {
        this.state.next(ComponentState.verify);
      } else if (result.status == CreateUserStatus.EXISTS) {
        this.notification.showError(this.translate.instant('SUBF_EMAIL_EXISTS'));
        this.state.next(ComponentState.form);
      } else if (result.status === CreateUserStatus.INVALID_EMAIL) {
        this.notification.showError(this.translate.instant('USER_ERROR_INVALID_EMAIL'));
        this.state.next(ComponentState.form);
      } else {
        this.notification.showError(this.translate.instant('ERROR_OCCURRED_UNKNOWN'));
        this.state.next(ComponentState.form);
      }
    });
  }

  onCodeSubmit() {
    this.state.next(ComponentState.wait);
    this.userService.checkVerificationCode(
          this.formGroup.value.email,
          this.codeFormGroup.value.code,
          this.PLAN_ID).pipe(
        switchMap(() => {
          const request: ISubscriptionRequest = {
            email: this.formGroup.value.email,
            planId: this.PLAN_ID,
            subscriptionId: '',
            licenseQuantity: 5,
            type: LicenseType.LICENSE_TYPE_TRIAL
          };
          return this.userService.createSubscriptionAndUser(request);
        }),
        catchError(() => {
          const title = this.translate.instant('INCORRECT_RESET_CODE_TITLE');
          const description = this.translate.instant('INCORRECT_RESET_CODE_DESC');
          this.notification.showError(description, title);
          this.state.next(ComponentState.verify);
          return EMPTY;
        })
    ).subscribe(response => {
      this.softwareVersion = response.software_versions;
      this.subscription = response.subscription;
      this.state.next(ComponentState.download);
    })
  }

  onCodeResend() {
    this.userService.resendVerificationEmail(this.formGroup.value.email)
        .subscribe(
        () => {
          this.notification.showSuccess(this.translate.instant('SUCCESS'));
        },
        error => {
          if (error.status === 429) {
            const seconds = error.headers.get('retry-after') || '300';
            this.notification.showError(
                this.translate.instant('EMV_TOO_MANY_REQUESTS', {seconds: seconds}));
          } else {
            this.notification.showError(this.translate.instant('ERROR_OCCURRED_UNKNOWN'));
          }
        });
  }

  showPasswordRequirements() {
    this.dialog.open(PasswordRulesComponent);
  }

  onCapsLockNewChanged(event: boolean) {
    this.capsOnNew = event && this.passwordInputIsFocused;
  }

  onCapsLockConfirmChanged(event: boolean) {
    this.capsOnConfirm = event && this.passwordConfirmInputIsFocused;
  }

  passwordFocus(id: string) {

    this.passwordInputIsFocused = false;
    this.passwordConfirmInputIsFocused = false;
    switch (id) {
      case "newPassInput":
        this.passwordInputIsFocused = true;
        break;
      case "confirmPassInput":
        this.passwordConfirmInputIsFocused = true;
        break;
    }
  }

  passwordBlur() {
    this.passwordInputIsFocused = false;
    this.passwordConfirmInputIsFocused = false;
  }
}
